import { adios, AsButton, useModal } from '@adserve/adserve-react-components';
import axios from 'axios';
import React, { useState, useEffect, useContext } from 'react';
import { starter2Api } from '../../../constants/Endpoint';
import { BookingApprovalStatusEnum, BusinessTypes, OffairStatuses, WorkflowTypes } from '../../../constants/Enums';
import { CreateBookingRequest } from '../../../services/WorkflowService';
import { cancelMessageButtonStyle, okMessageButtonStyle } from '../CampaignStyles';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';

const buttonStyle = {
    width: 85,
    height: 34,
    fontSize: 15,
    fontWeight: 500,
    boxSizing: 'border-box',
    marginTop: 9,
    marginLeft: 15,
    marginBottom: 8,
    marginRight: 13
};

const BookButton = ({
    campaign,
    data,
    readOnly,
    getQuotes,
    refreshCampaign,
    creditStatusWorkflowId,
    preBookingWorkflowId,
    bookingWorkflowId,
    discountApprovalWorkflowId,
    isCampaignModified,
    updateCampaign,
    updateActiveRequest,
    campaignApprovalTeam,
    businessType,
    validateBooking,
    billingAccountCreditDetails,
    isCampaignBurst,
    activeRequest,
    discountRequest,
    dealDiscounts = [],
    onDiscountRequestApproval,
    isUsingDynamicWorkflow,
    valuePotRequest,
    valuePotApprovalWorkflowId
}) => {
    const { addMessage } = useModal();
    const [disabled, setDisabled] = useState(true);
    const [workflowId, setWorkflowId] = useState();
    const { selectedProperties, offairActivities, hasAirtimeExpired, billingAccountChanged } = useContext(AirtimePlanningContext);

    useEffect(() => {
        setDisabled(readOnly || (data.status !== 5 && data.status !== 8 && !billingAccountChanged));
    }, [readOnly, data.statusName, data.campaignStatusName, campaign?.currentPlan?.reservedUntil]);

    useEffect(() => {
        if (!preBookingWorkflowId) {
            setWorkflowId(bookingWorkflowId);
        } else if (preBookingWorkflowId && data.approvalStatus === BookingApprovalStatusEnum.PREAPPROVED) {
            setWorkflowId(bookingWorkflowId);
        } else {
            setWorkflowId(preBookingWorkflowId);
        }
    }, [data.approvalStatus, preBookingWorkflowId, bookingWorkflowId]);

    const sendBookingApprovalRequest = () => {
        // Having to do this because of the mess created by having two workflowTypes in the system
        var workflowType = businessType.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase()
            ? data.approvalStatus === null
                ? preBookingWorkflowId
                    ? WorkflowTypes.SANDPPRECAMPAIGNBOOKING
                    : WorkflowTypes.SANDPPOSTCAMPAIGNBOOKING
                : WorkflowTypes.SANDPPOSTCAMPAIGNBOOKING
            : WorkflowTypes.PRECAMPAIGNBOOKING;

        var usesDiscounts = false;

        dealDiscounts?.forEach((disc) => {
            if (disc?.hasOwnProperty('campaignPercentageValue')) {
                if (disc?.campaignPercentageValue !== '') {
                    usesDiscounts = true;
                }
            }
        })

        if (usesDiscounts && Object.keys(discountRequest)?.length < 1  && discountApprovalWorkflowId !== null) {
            const discount = dealDiscounts.reduce(
                (prev, cur) =>
                    prev +
                    (cur.campaignPercentageValue > 0
                        ? cur.campaignPercentageValue
                        : 0),
                0
            );

            // If we have a discount but didn't request yet, create the request now so it can be found when approving the pre-campaign booking
            onDiscountRequestApproval(discount);
        }

        setDisabled(true);
        CreateBookingRequest(workflowId, data.campaignId, data.id, campaignApprovalTeam, workflowType)
            .then((result) => {
                if (refreshCampaign) {
                    refreshCampaign();
                }

                if (updateActiveRequest) {
                    updateActiveRequest(workflowId, data.campaignId);
                }

                addMessage({
                    message: window.translate(
                        result.id ? 'Request submitted successfully' : 'Request submission failed'
                    ),
                    buttons: [
                        {
                            text: window.translate('Ok')
                        }
                    ]
                });
            })
            .catch((error) => {
                setDisabled(false);
                addMessage({
                    message: window.translate(`Campaign cannot be booked because:\n${error.response.data}`),
                    buttons: [
                        {
                            text: window.translate('Ok'),
                            style: { width: 87 },
                            callback: null
                        }
                    ]
                });
            });
    };

    const handleApprovalClick = async () => {

        // There is a discount but it's not complete
        if (Object.keys(discountRequest)?.length > 0) {
            if (!discountRequest?.isComplete) {
                addMessage({
                    message: window.translate(
                        'Cannot request approval as custom discount is awaiting approval'
                    ),
                    buttons: [
                        {
                            text: window.translate('OK'),
                            style: okMessageButtonStyle,
                        }
                    ]
                });
                return;
            }
        }

        var usesDiscounts = false;

        dealDiscounts?.forEach((disc) => {
            if (disc?.hasOwnProperty('campaignPercentageValue')) {
                if (disc?.campaignPercentageValue !== '') {
                    usesDiscounts = true;
                }
            }
        })

        // The discount table has entries but the discount request hasn't been created
        if (usesDiscounts && Object.keys(discountRequest)?.length < 1 && discountApprovalWorkflowId !== null) {
            addMessage({
                message: window.translate(
                    'Cannot request approval as custom discount is given but not requested for approval'
                ),
                buttons: [
                    {
                        text: window.translate('OK'),
                        style: okMessageButtonStyle,
                    }
                ]
            });
            return;
        }

        // There is a value pot request but it's not complete
        if (Object.keys(valuePotRequest)?.length > 0) {
            if (!valuePotRequest?.isComplete) {
                addMessage({
                    message: window.translate(
                        'Cannot request approval as value pot is awaiting approval'
                    ),
                    buttons: [
                        {
                            text: window.translate('OK'),
                            style: okMessageButtonStyle,
                        }
                    ]
                });
                return;
            }
        }

        // The value pot has been updated but the request hasn't been created
        if (campaign?.valuePotSpendPct && Object.keys(valuePotRequest)?.length < 1 && valuePotApprovalWorkflowId !== null) {
            addMessage({
                message: window.translate(
                    'Cannot request approval as value pot has changed but not requested for approval'
                ),
                buttons: [
                    {
                        text: window.translate('OK'),
                        style: okMessageButtonStyle,
                    }
                ]
            });
            return;
        }

        if (creditStatusWorkflowId) {
            if (billingAccountCreditDetails?.creditStatusShortRef !== 'A') {
                const message = 'Cannot book this campaign as the customer is not credit-approved';
    
                addMessage({
                    message: window.translate(message),
                    buttons: [
                        {
                            text: window.translate('Ok'),
                            style: { width: 87 },
                            callback: null
                        }
                    ]
                });
                return;
            }
        }

        if (!isCampaignModified) {

            sendBookingApprovalRequest();
            return;
        }

        addMessage({
            message: window.translate(
                'You have unsaved changes.  These need to be saved before requesting approval. Do you wish to save your changes now?'
            ),
            buttons: [
                {
                    text: window.translate('No'),
                    style: { ...cancelMessageButtonStyle, width: 132 }
                },
                {
                    text: window.translate('Yes'),
                    style: okMessageButtonStyle,
                    callback: async () => {
                        const result = await updateCampaign();

                        if (result) {
                            sendBookingApprovalRequest();
                        }
                    }
                }
            ]
        });
    };


    const doBooking = (force = false) => {
        if (!force && campaign.jcn && !data.sentToJet) {
            addMessage({
                message: window.translate('This quote hasn\'t yet been sent to J-ET. Are you sure you wish to continue?'),
                buttons: [
                    {
                        text: window.translate('No'),
                        style: { ...cancelMessageButtonStyle, width: 132 }
                    },
                    {
                        text: window.translate('Yes'),
                        style: okMessageButtonStyle,
                        callback: () => doBooking(true)
                    }
                ]
            });
            return;
        }
        addMessage({
            message: window.translate('Are you sure you want to book this quote?'),
            buttons: [
                {
                    text: window.translate('No'),
                    style: { width: 87 },
                    callback: null
                },
                {
                    text: window.translate('Yes'),
                    style: { cursor: 'pointer' },
                    callback: async () => {
                        setDisabled(true);
                        adios(() =>
                            axios.post(`${starter2Api}/bookingRequest`, {
                                quoteId: data.id
                            })
                        )
                            .then(() => {
                                refreshCampaign();
                                getQuotes();
                            })
                            .catch((error) => {
                                setDisabled(false);
                                addMessage({
                                    message: window.translate(error.response.data.title || error.message),
                                    buttons: [
                                        {
                                            text: window.translate('Ok'),
                                            style: { width: 87 },
                                            callback: null
                                        }
                                    ]
                                });
                            });
                    }
                }
            ]
        });
    };

    const handleBookClick = async () => {
        if (disabled) return;

        var usesDiscounts = false;

        dealDiscounts?.forEach((disc) => {
            if (disc?.hasOwnProperty('campaignPercentageValue')) {
                if (disc?.campaignPercentageValue !== '') {
                    usesDiscounts = true;
                }
            }
        })

        // If a discount is expected to be applied but not approved, handle it here
        if ((Object.keys(discountRequest)?.length > 0 || usesDiscounts) && discountApprovalWorkflowId !== null) {
            if ((discountRequest?.state?.stateType !== 1 && discountRequest?.state?.stateType !== '1' && discountRequest?.state?.stateType !== 'Approved') || !discountRequest?.isComplete) {
                addMessage({
                    message: window.translate(Object.keys(discountRequest)?.length > 0 ? 'Cannot book this campaign as custom discount is awaiting approval' : 'Cannot book this campaign as discount approval must be requested'),
                    buttons: [
                        {
                            text: window.translate('Ok'),
                            style: { width: 87 },
                            callback: null
                        }
                    ]
                });
                return;
            }
        }

        // If value pot is to be applied but not approved, handle it here
        if ((Object.keys(valuePotRequest)?.length > 0 || campaign.valuePotSpendPct) && valuePotApprovalWorkflowId !== null) {
            if ((valuePotRequest?.state?.stateType !== 1 && valuePotRequest?.state?.stateType !== '1' && valuePotRequest?.state?.stateType !== 'Approved') || !valuePotRequest?.isComplete) {
                addMessage({
                    message: window.translate(Object.keys(valuePotRequest)?.length > 0 ? 'Cannot book this campaign as value pot is awaiting approval' : 'Cannot book this campaign as value pot approval must be requested'),
                    buttons: [
                        {
                            text: window.translate('Ok'),
                            style: { width: 87 },
                            callback: null
                        }
                    ]
                });
                return;
            }
        }

        if (creditStatusWorkflowId) {
            if (billingAccountCreditDetails?.creditStatusShortRef !== 'A') {
                const message = 'Cannot book this campaign as the customer is not credit-approved';
                addMessage({
                    message: window.translate(message),
                    buttons: [
                        {
                            text: window.translate('Ok'),
                            style: { width: 87 },
                            callback: null
                        }
                    ]
                });
                return;
            }
        }

        // Has been commented as this will be implemented once we get the account balances from SAP
        // if (billingAccountCreditDetails?.creditLimit > 0 && (billingAccountCreditDetails?.availableCredit - data.amount) < 0) {
        //     const message = 'There is insufficient credit available to book this quote';
        //     addMessage({
        //         message: window.translate(message),
        //         buttons: [
        //             {
        //                 text: window.translate('Ok'),
        //                 style: { width: 87 },
        //                 callback: null
        //             }
        //         ]
        //     });
        //     return;
        // }

        if (typeof validateBooking === 'function') {
            if (!validateBooking()) {
                return;
            }
        }

        if (!isCampaignModified) {
            doBooking();
            return;
        }

        addMessage({
            message: window.translate(
                'You have unsaved changes.  These need to be saved before booking. Do you wish to save your changes now?'
            ),
            buttons: [
                {
                    text: window.translate('No'),
                    style: { ...cancelMessageButtonStyle, width: 132 }
                },
                {
                    text: window.translate('Yes'),
                    style: okMessageButtonStyle,
                    callback: async () => {
                        const result = await updateCampaign();

                        if (result) {
                            doBooking();
                        }
                    }
                }
            ]
        });
    };

    return disabled && data?.status !== 8 && data?.status !== 5 ? (
        window.translate(data.statusName)
    ) : workflowId && !isCampaignBurst &&
      data.approvalStatus === null 
      ? (
            <AsButton
                id={`approvalButton${data.id}`}
                style={{ ...buttonStyle, fontSize: 13 }}
                disabled={disabled || (campaign.businessType.toLowerCase() !== BusinessTypes.SANDP.name.toLowerCase() && hasAirtimeExpired()) || data?.status === 8 || (isUsingDynamicWorkflow && (!campaignApprovalTeam || campaignApprovalTeam === null))}
                handleClick={handleApprovalClick}
            >
                {window.translate('Request Approval')}
            </AsButton>
        ) : (bookingWorkflowId && data.approvalStatus === BookingApprovalStatusEnum.PREAPPROVED) 
        ? (
            <AsButton
                id={`approvalButton${data.id}`}
                style={{ ...buttonStyle, fontSize: 13 }}
                disabled={disabled || (campaign.businessType.toLowerCase() !== BusinessTypes.SANDP.name.toLowerCase() && hasAirtimeExpired()) || data?.status === 8 || (isUsingDynamicWorkflow && (!campaignApprovalTeam || campaignApprovalTeam === null))}
                handleClick={handleApprovalClick}
            >
                {window.translate('Book')}
            </AsButton>
        )
        : workflowId && !isCampaignBurst &&
      data.approvalStatus === BookingApprovalStatusEnum.AWAITING_APPROVAL ? (
                window.translate('Requested Approval')
            ) : workflowId && !isCampaignBurst &&
      data.approvalStatus === BookingApprovalStatusEnum.REJECTED ? (
                    <AsButton id={`rejectedButton${data.id}`} style={buttonStyle} disabled={true}>
                        {window.translate('Rejected')}
                    </AsButton>
                ) : (
                    <AsButton
                        id={`bookButton${data.id}`}
                        style={buttonStyle}
                        disabled={disabled
                            || ( campaign.businessType.toLowerCase() !== BusinessTypes.SANDP.name.toLowerCase() && hasAirtimeExpired())
                            || data?.status === 8
                            || (isUsingDynamicWorkflow && !campaignApprovalTeam && !isCampaignBurst)
                            || ((businessType.toLowerCase() === BusinessTypes.OFFAIRS.name.toLowerCase()
                                || businessType.toLowerCase() === BusinessTypes.DIGITAL.name.toLowerCase())
                                && offairActivities.every((oa) => oa.quoteId && oa.quoteId !== window.emptyGuid && oa.status === OffairStatuses.CANCELLED))
                        }
                        handleClick={handleBookClick}
                    >
                        {window.translate('Book')}
                    </AsButton>
                );
};

export default BookButton;
